import { useEffect, useState, type FormEvent } from 'react';
import { matchPath, NavLink, Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { LoadingBlock } from '@/components/PageHeader';
import { useSafeAccess } from '@/lib/access';
import { useLogout, useSession } from '@/lib/auth/session';
import { env } from '@/lib/env';
import { useSites } from '@/lib/queries';
import { JOURNAL_TYPES } from '@/pages/safe/safeJournalApi';
import { SITE_MENU_GROUPS, sitePath } from '@/pages/site/siteMenu';

const SIDEBAR_WIDTH = 208; // front · vendor 의 lg:pl-52 와 같은 값

const MENU: Array<{ to: string; label: string }> = [
  { to: '/dashboard', label: '대시보드' },
  { to: JOURNAL_TYPES.EDUCATION.path, label: JOURNAL_TYPES.EDUCATION.label },
  { to: JOURNAL_TYPES.HEALTH.path, label: JOURNAL_TYPES.HEALTH.label },
  { to: JOURNAL_TYPES.PATROL.path, label: JOURNAL_TYPES.PATROL.label },
  { to: JOURNAL_TYPES.JOINT.path, label: JOURNAL_TYPES.JOINT.label },
  { to: JOURNAL_TYPES.TBM.path, label: JOURNAL_TYPES.TBM.label },
  { to: JOURNAL_TYPES.RISK.path, label: JOURNAL_TYPES.RISK.label },
  { to: '/legacy', label: '기존 데이터' },
  { to: '/cases', label: '조치 사례' },
  { to: '/accounts', label: '계정 관리' },
];

function linkClass({ isActive }: { isActive: boolean }): string {
  const base = 'flex items-center gap-2.5 rounded-md px-3 py-2.5 text-ui-md transition-colors';
  return isActive ? `${base} font-medium` : base;
}

function linkStyle({ isActive }: { isActive: boolean }) {
  return isActive ? { background: 'var(--accent-bg)', color: 'var(--accent)' } : { color: 'var(--text-secondary)' };
}

/**
 * 현장 화면(`/sites/:siteId/…`)에서는 메뉴가 그 현장의 세부 메뉴로 바뀐다(DOC-135 · DOC-136 묶음). 맨 위는 돌아가는 길 —
 * 안전 권한이면 대시보드, 배정 현장만 보는 사람은 내 현장 목록. 맨 아래는 그룹웨어 현장 조회.
 */
function SiteSidebar({ siteId, onNavigate }: { siteId: string; onNavigate?: () => void }) {
  const sites = useSites();
  const access = useSafeAccess();
  const name = sites.data?.find((site) => site.id === siteId)?.name ?? '';
  const back = access.all ? { to: '/dashboard', label: '← 전체 현장' } : access.siteIds.length > 1 ? { to: '/my-sites', label: '← 내 현장' } : null;
  // 메뉴가 길어 세로로 넘친다 — 줄이 눌려 찌그러지지 않게 자식은 줄어들지 않는다(shrink-0).
  return (
    <nav className="flex h-full flex-col gap-1 overflow-y-auto p-3 [&>*]:shrink-0">
      {back && (
        <NavLink to={back.to} className="px-3 py-1.5 text-ui-xs" style={{ color: 'var(--text-secondary)' }} onClick={onNavigate}>
          {back.label}
        </NavLink>
      )}
      <div className="truncate px-3 pb-2 pt-1 text-ui-md font-semibold" style={{ color: 'var(--text-primary)' }} title={name}>
        {name}
      </div>
      {SITE_MENU_GROUPS.map((group) => (
        <div key={group.label ?? 'top'} className="flex flex-col gap-1">
          {group.label && (
            <div className="px-3 pb-1 pt-3 text-ui-xs font-semibold" style={{ color: 'var(--text-muted)' }}>
              {group.label}
            </div>
          )}
          {group.items.map((item) => (
            <NavLink key={item.key} to={sitePath(siteId, item.key)} className={linkClass} style={linkStyle} onClick={onNavigate}>
              {item.label}
            </NavLink>
          ))}
        </div>
      ))}
      <a
        href={`${env.loungeUrl}/sites/pmis?site=${encodeURIComponent(siteId)}`}
        className="mt-auto px-3 py-2 text-ui-xs"
        style={{ color: 'var(--text-secondary)' }}
      >
        그룹웨어 현장 조회 ↗
      </a>
    </nav>
  );
}

/** 배정 현장만 보는 사람의 메뉴 — 자기 현장들. */
function MySitesSidebar({ siteIds, onNavigate }: { siteIds: string[]; onNavigate?: () => void }) {
  const sites = useSites();
  return (
    <nav className="flex h-full flex-col gap-1 overflow-y-auto p-3">
      {(sites.data ?? [])
        .filter((site) => siteIds.includes(site.id))
        .map((site) => (
          <NavLink key={site.id} to={sitePath(site.id)} className={linkClass} style={linkStyle} onClick={onNavigate}>
            {site.name}
          </NavLink>
        ))}
    </nav>
  );
}

function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { pathname } = useLocation();
  const access = useSafeAccess();
  const siteId = matchPath('/sites/:siteId/*', pathname)?.params.siteId;
  if (siteId) return <SiteSidebar siteId={siteId} onNavigate={onNavigate} />;
  if (!access.all) return <MySitesSidebar siteIds={access.siteIds} onNavigate={onNavigate} />;
  return (
    <nav className="flex h-full flex-col gap-1 overflow-y-auto p-3">
      {MENU.map((item) => (
        <NavLink key={item.to} to={item.to} className={linkClass} style={linkStyle} onClick={onNavigate}>
          {item.label}
        </NavLink>
      ))}
    </nav>
  );
}

/**
 * 앱 껍데기 — 머리줄 · 좌측 메뉴 · 본문. 협력사 포털(vendor `AppShell`)과 같은 틀이다.
 *
 * <p>사이드바가 자리를 차지하는 지점은 lg(1024)다. 그보다 좁으면 서랍으로 물러난다.
 * 본문은 전폭이다 — 일지는 A4 종이를 펼치고 대시보드는 표가 넓다.
 */
/**
 * 머리줄 검색(2026-11-04 「검색을 굳이 메뉴로 뺄 이유가 있나, 상단 헤더에」). Enter 로 결과 화면에 간다 — 현장 화면에서는
 * 그 현장만(`/sites/:id/search`), 그 밖에서는 볼 수 있는 현장 전부(`/search`). 검색어는 주소(`?q=`)에 실려 뒤로 가기 ·
 * 새로고침에도 남는다. 비우고 Enter 면 최근 기록, 한 글자는 막는다(서버와 같은 규칙).
 */
function HeaderSearch({ siteId }: { siteId?: string }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const onResults = location.pathname.endsWith('/search');
  const current = onResults ? (params.get('q') ?? '') : '';
  const [draft, setDraft] = useState(current);
  useEffect(() => setDraft(current), [current]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const q = draft.trim();
    if ([...q].length === 1) return;
    const base = siteId ? sitePath(siteId, 'search') : '/search';
    navigate(q ? `${base}?q=${encodeURIComponent(q)}` : base);
  };

  return (
    <form role="search" onSubmit={submit} className="ml-2 min-w-0 flex-1 max-sm:mr-1" style={{ maxWidth: '420px' }}>
      <input
        type="search"
        className="input w-full"
        style={{ height: '36px', padding: '0 12px', fontSize: '14px' }}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="검색"
        aria-label="검색어"
      />
    </form>
  );
}

export function AppShell() {
  const session = useSession();
  const logout = useLogout();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  const user = session.data?.user ?? null;
  const access = useSafeAccess();
  // 안전 권한이면 어디든, 아니면 자기 현장 화면 · 내 현장 목록만(DOC-136 §6). 판정은 서버가 요청마다 다시 한다.
  const siteId = matchPath('/sites/:siteId/*', location.pathname)?.params.siteId;
  const allowed =
    access.all || (siteId !== undefined && access.siteIds.includes(siteId)) || (location.pathname === '/my-sites' && access.siteIds.length > 0);

  return (
    <div className="min-h-screen">
      <header
        className="fixed inset-x-0 top-0 z-30 flex h-14 items-center gap-2 border-b px-3"
        style={{ background: 'var(--bg-card)', borderColor: 'var(--border)' }}
      >
        <button
          type="button"
          className="rounded-md p-2 lg:hidden"
          style={{ color: 'var(--text-secondary)' }}
          aria-label="메뉴"
          onClick={() => setDrawerOpen(true)}
        >
          ☰
        </button>
        {/*
          폰(앱의 기준 · DOC-140)에서는 로고가 줄지 않고 이름 칸이 남는 폭만 쓴다 — 전에는 로고 쪽이 먼저 줄어
          「Safe」 가 「S…」 로 잘렸다(2026-10-29 폰 크기 실측).
        */}
        <NavLink to="/" className="flex shrink-0 items-center gap-2 lg:w-52">
          <img src="/brand/bomi-ci.png" alt="BoMI" style={{ height: 22 }} />
          <span className="text-ui-md font-semibold" style={{ color: 'var(--text-primary)' }}>
            Safe
          </span>
        </NavLink>

        {user && <HeaderSearch siteId={siteId} />}

        {user && (
          <span className="ml-auto hidden min-w-0 flex-1 truncate text-right text-ui-xs sm:block" style={{ color: 'var(--text-secondary)' }}>
            {user.name}
            {user.rank ? ` ${user.rank}` : ''}
          </span>
        )}
        <button
          type="button"
          className={`btn-secondary shrink-0 px-3 py-1.5 text-ui-xs ${user ? '' : 'ml-auto'}`}
          onClick={() =>
            logout.mutate(undefined, {
              onSettled: () => window.location.assign('/'),
            })
          }
        >
          로그아웃
        </button>
      </header>

      <aside
        className="fixed bottom-0 left-0 top-14 z-20 hidden overflow-hidden border-r lg:block"
        style={{ width: SIDEBAR_WIDTH, background: 'var(--bg-card)', borderColor: 'var(--border)' }}
      >
        <Sidebar />
      </aside>

      {drawerOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <button
            type="button"
            aria-label="메뉴 닫기"
            className="absolute inset-0"
            style={{ background: 'rgba(15, 23, 42, 0.4)' }}
            onClick={() => setDrawerOpen(false)}
          />
          <aside
            className="absolute bottom-0 left-0 top-0 overflow-hidden border-r"
            style={{ width: SIDEBAR_WIDTH, background: 'var(--bg-card)', borderColor: 'var(--border)' }}
          >
            <Sidebar onNavigate={() => setDrawerOpen(false)} />
          </aside>
        </div>
      )}

      <main className="px-4 pb-8 pt-16 lg:pl-[calc(208px+1rem)]">
        {!access.ready ? (
          <LoadingBlock />
        ) : allowed ? (
          <Outlet />
        ) : (
          <div className="card p-6 text-ui-sm" style={{ color: 'var(--text-secondary)' }}>
            안전 관리 권한이 없습니다.
          </div>
        )}
      </main>
    </div>
  );
}
